import { useDrag } from '@/hooks/useDrag';
import { searchAssistantUIStore } from '@/stores/searchAssistantStore';
import { useState } from 'react';

const styles = {
  draggableWrapper: {
    position: 'fixed',
    left: 0,
    top: 0,
    transform: 'translate3d(var(--x, 0px), var(--y, 0px), 0)',
    transition: 'transform 0.2s var(--ease-move)',
    zIndex: 20,
  },
  isDragging: {
    transition: 'none',
  },
};

/**
 * A wrapper component that makes its children draggable with smooth animations.
 * Uses CSS variables and translate3d for hardware-accelerated positioning.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The content to be made draggable
 * @param {React.ReactNode} props.dragHandle - Drag handle element
 * @param {number} props.elementWidth - Width of the draggable element for boundary calculations
 * @param {number} props.elementHeight - Height of the draggable element for boundary calculations
 * @param {Object} props.style - Inline styles to apply
 * @param {...Object} props - Additional props passed to the wrapper div
 *
 * @example
 * // Basic draggable wrapper
 * <DraggableWrapper>
 *   <div>Drag me anywhere!</div>
 * </DraggableWrapper>
 *
 * @example
 * // With custom drag handle
 * <DraggableWrapper
 *   dragHandle={<div className="drag-handle">≡</div>}
 *   elementWidth={400}
 *   elementHeight={300}
 * >
 *   <div>Content here</div>
 * </DraggableWrapper>
 *
 * @example
 * // With custom styling
 * <DraggableWrapper
 *   style={{ zIndex: 1000 }}
 *   elementWidth={500}
 *   elementHeight={400}
 * >
 *   <MyComponent />
 * </DraggableWrapper>
 */
const ForDesktop = ({ children, dragHandle, elementWidth, elementHeight, style, ...props }) => {
  const storePosition = searchAssistantUIStore((state) => state.position);
  const setPosition = searchAssistantUIStore.use.setPosition();
  const [currentPosition, setCurrentPosition] = useState(storePosition);

  const { ref: dragRef, isDragging } = useDrag({
    onDragMove: setCurrentPosition,
    onDragEnd: setPosition,
    initialPosition: storePosition,
    elementWidth,
    elementHeight,
  });

  if (
    !isDragging &&
    (currentPosition.x !== storePosition.x || currentPosition.y !== storePosition.y)
  ) {
    setCurrentPosition(storePosition);
  }

  return (
    <div
      style={{
        ...style,
        ...styles.draggableWrapper,
        ...(isDragging ? styles.isDragging : {}),
        '--x': `${currentPosition.x}px`,
        '--y': `${currentPosition.y}px`,
      }}
      {...props}
    >
      {dragHandle ? <div ref={dragRef}>{dragHandle}</div> : <div ref={dragRef}>{children}</div>}
      {dragHandle && children}
    </div>
  );
};

const ForMobile = ({ children }) => {
  const storePosition = searchAssistantUIStore((state) => state.position);

  return (
    <div
      style={{
        '--x': `${storePosition.x}px`,
        '--y': `${storePosition.y}px`,
        ...styles.draggableWrapper,
      }}
    >
      {children}
    </div>
  );
};

const DraggableWrapper = ({ children, ...props }) => {
  const isMobile = searchAssistantUIStore((state) => state.isMobile);
  return isMobile ? (
    <ForMobile>{children}</ForMobile>
  ) : (
    <ForDesktop {...props}>{children}</ForDesktop>
  );
};

export { DraggableWrapper };
